import { useCallback, useMemo } from "react"
import { useMutation } from "@tanstack/react-query"
import { useAnalytics } from "connected-components/analytics"
import { log } from "helpers/logger"
import { Creators } from "model/apiWrapper"
import moment from "moment"
import { useDispatch } from "react-redux"
import { useAccountAlias } from "resources/account"
import { v4 } from "uuid"
import { AI_TEAMMATE_EVENTS } from "../analytics"
import { MESSAGE_ROLES } from "../helpers/constants"
import { useAiTeammateContext } from "./context"
import { useLastAssistantMessageId } from "./selectors"

export default function useSendMessage() {
  const {
    modelId,
    resourceMetadata,
    instrument,
    base,
    startDate,
    endDate,
    threadId,
    setThreadId,
    addMessage,
    error,
    setError,
    removeLastUserMessage,
  } = useAiTeammateContext()
  const analytics = useAnalytics()
  const accountAlias = useAccountAlias()
  const lastAssistantMessageId = useLastAssistantMessageId()
  const dispatch = useDispatch()

  const { isPending, mutate } = useMutation({
    mutationFn: ({ messageText }) => {
      const agentArguments = {
        modelId,
        positionSet: {
          id: resourceMetadata?.alias || instrument.id,
          type: instrument.type,
        },
        base,
        startDate,
        endDate,
      }

      const payload = { message: messageText, agentArguments }
      if (threadId) {
        payload.lastAssistantMsgId = lastAssistantMessageId
      }

      return new Promise((resolve, reject) => {
        let apiUrl = `/api/${accountAlias}/ai/chat/threads`
        if (threadId) {
          apiUrl += `/${threadId}`
        }
        dispatch(Creators.post(apiUrl, payload, resolve, reject))
      })
    },
  })

  const handleSuccessResponse = useCallback((data) => {
    if (!threadId) {
      analytics.track(AI_TEAMMATE_EVENTS.NEW_THREAD_CREATED, { threadId: data.threadId })
    }

    setThreadId(data.threadId)
    addMessage(data.assistantMessage)
  }, [threadId, setThreadId, addMessage, analytics])

  const handleErrorResponse = useCallback(({ error }) => {
    setError(error)
    log(error)
  }, [setError])

  const sendMessage = useCallback((messageText) => {
    if (error) {
      setError(null)
      removeLastUserMessage()
    }

    const userMessage = {
      id: v4(),
      createdAt: moment().utc().toISOString(),
      text: messageText,
      role: MESSAGE_ROLES.USER,
    }
    addMessage(userMessage)

    mutate({ messageText }, {
      onSuccess: handleSuccessResponse,
      onError: handleErrorResponse,
    })

    analytics.track(AI_TEAMMATE_EVENTS.MESSAGE_SENT, { messageText })
  }, [addMessage, error, removeLastUserMessage, mutate, setError, handleSuccessResponse, handleErrorResponse, analytics])

  const value = useMemo(() => ({
    isSendingMessage: isPending,
    sendMessage,
  }), [isPending, sendMessage])

  return value
}
